import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Shield, TrendingUp, Building2, DollarSign, Users, CheckCircle, BarChart3, MapPin, Lock } from "lucide-react";
import { fadeUp, Section, Card, GoldButton } from "../components/shared/UIComponents";

const CO_INVEST = [
  "Deal-by-deal equity participation",
  "Minimum commitment: $100,000",
  "Full transparency and reporting",
  "Direct ownership interest",
  "Accredited investors only (SEC Rule 501)",
];

const FUND = [
  "Diversified Texas CRE exposure",
  "Targeted 10-14% annual returns",
  "Minimum commitment: $50,000",
  "Quarterly distributions",
  "Accredited investors only (SEC Rule 501)",
];

const PROCESS = [
  { icon: Building2, title: "Source", desc: "We source off-market and direct acquisition opportunities through proprietary channels and deep broker relationships." },
  { icon: Shield, title: "Underwrite", desc: "Every deal is stress-tested with AI-powered market analysis, comparable sales data, and conservative cash flow projections." },
  { icon: BarChart3, title: "Structure", desc: "We structure each transaction to maximize risk-adjusted returns — direct purchase, assignment, or joint venture." },
  { icon: TrendingUp, title: "Manage", desc: "Active asset management drives value. We improve operations, execute renovations, and optimize for long-term cash flow." },
];

const TEXAS_THESIS = [
  { stat: "#1", label: "Fastest growing state by population (US Census Bureau)" },
  { stat: "29M+", label: "Residents with continued in-migration from CA, NY, IL" },
  { stat: "No", label: "State income tax — favorable capital environment" },
  { stat: "Top 5", label: "Texas metros in US job growth (BLS)" },
];

export default function InvestorsPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", investor_type: "", accredited: "", capital_range: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/investors/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) setSubmitted(true);
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#EDE8DC] bg-white text-sm text-[#0A0A0A] placeholder-[#bbb] focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 transition-colors";
  const labelCls = "block text-xs font-bold uppercase tracking-[0.08em] text-[#888] mb-1.5";

  return (
    <>
      {/* Hero */}
      <section className="relative py-20 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gold/10 rounded-full blur-[150px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold mb-4">Capital Partners</p>
            <h1 className="text-4xl font-bold text-cream sm:text-5xl md:text-6xl tracking-tight">Partner With <span className="luxury-text-gradient">Imaani Capital</span></h1>
            <p className="mt-5 max-w-2xl text-base text-cream/70 leading-relaxed sm:text-lg">
              Deploy capital alongside a disciplined Texas-based acquisition team. Co-invest deal-by-deal or through our private credit fund.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Two Tracks */}
      <Section eyebrow="Investment Vehicles" title="Two Ways to Invest">
        <div className="grid gap-8 lg:grid-cols-2">
          <Card className="!p-6 sm:!p-8 !border-emerald-300 ring-1 ring-emerald-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0A0A0A]">Co-Investment</h3>
                <p className="text-xs text-[#888]">Deal-by-deal equity participation</p>
              </div>
            </div>
            <ul className="space-y-2.5 mb-6">
              {CO_INVEST.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#555]">
                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {c}
                </li>
              ))}
            </ul>
            <div className="text-2xl font-bold text-emerald-700 mb-1">$100K</div>
            <p className="text-xs text-[#888]">Minimum commitment per deal</p>
          </Card>

          <Card className="!p-6 sm:!p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-200 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0A0A0A]">Private Credit Fund</h3>
                <p className="text-xs text-[#888]">Diversified Texas CRE</p>
              </div>
            </div>
            <ul className="space-y-2.5 mb-6">
              {FUND.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#555]">
                  <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <div className="text-2xl font-bold text-amber-700 mb-1">10-14%</div>
            <p className="text-xs text-[#888]">Targeted annual returns</p>
          </Card>
        </div>
      </Section>

      {/* Our Process */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-10">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold">How We Operate</p>
            <h2 className="mt-3 text-3xl font-bold text-cream sm:text-4xl">Our Process</h2>
          </motion.div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm p-6 hover:border-gold/30 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-emerald-800/50 border border-emerald-700/50 flex items-center justify-center mb-4">
                  <p.icon className="h-5 w-5 text-gold" />
                </div>
                <h3 className="text-base font-bold text-cream mb-2">{p.title}</h3>
                <p className="text-sm text-cream/70 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Texas */}
      <Section eyebrow="Market Thesis" title="Why Texas & the Sun Belt">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {TEXAS_THESIS.map((t, i) => (
            <Card key={i} className="!p-5 text-center">
              <div className="text-2xl font-bold text-emerald-700 mb-1">{t.stat}</div>
              <p className="text-xs text-[#888] leading-relaxed">{t.label}</p>
            </Card>
          ))}
        </div>
        <p className="text-sm text-[#555] leading-relaxed max-w-3xl">
          Texas offers a rare combination of population growth, economic diversification, business-friendly regulation, and housing demand that creates consistent opportunity for commercial real estate investors. Our team's deep local knowledge gives us a measurable edge in sourcing, underwriting, and managing assets in this market.
        </p>
      </Section>

      {/* Registration Form */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-10 text-center">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold">Get Started</p>
            <h2 className="mt-3 text-3xl font-bold text-cream sm:text-4xl">Join Our Network</h2>
            <p className="mt-3 text-base text-cream/70">Register your interest and our team will be in touch.</p>
          </motion.div>

          {submitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-gold" />
              </div>
              <h3 className="text-2xl font-bold text-cream mb-3">Registration Received</h3>
              <p className="text-base text-cream/70 max-w-md mx-auto">Thank you for your interest. Our investor relations team will contact you within 48 hours.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm p-6 sm:p-8" data-testid="investor-register-form">
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-cream/60 mb-1.5">Full Name *</label>
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Your name" className="w-full px-4 py-3 rounded-xl bg-emerald-950/60 border border-emerald-700/40 text-sm text-cream placeholder-cream/40 focus:outline-none focus:border-gold/60 transition-colors" data-testid="investor-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-cream/60 mb-1.5">Phone</label>
                    <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(xxx) xxx-xxxx" className="w-full px-4 py-3 rounded-xl bg-emerald-950/60 border border-emerald-700/40 text-sm text-cream placeholder-cream/40 focus:outline-none focus:border-gold/60 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.08em] text-cream/60 mb-1.5">Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="you@email.com" className="w-full px-4 py-3 rounded-xl bg-emerald-950/60 border border-emerald-700/40 text-sm text-cream placeholder-cream/40 focus:outline-none focus:border-gold/60 transition-colors" data-testid="investor-email" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-cream/60 mb-1.5">Investor Type</label>
                    <select value={form.investor_type} onChange={e => setForm({ ...form, investor_type: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-emerald-950/60 border border-emerald-700/40 text-sm text-cream focus:outline-none focus:border-gold/60 transition-colors">
                      <option value="">Select</option>
                      <option value="individual">Individual</option>
                      <option value="family_office">Family Office</option>
                      <option value="fund">Fund / Institutional</option>
                      <option value="ria">RIA / Wealth Manager</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-cream/60 mb-1.5">Capital Range</label>
                    <select value={form.capital_range} onChange={e => setForm({ ...form, capital_range: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-emerald-950/60 border border-emerald-700/40 text-sm text-cream focus:outline-none focus:border-gold/60 transition-colors">
                      <option value="">Select</option>
                      <option value="50k-100k">$50K - $100K</option>
                      <option value="100k-500k">$100K - $500K</option>
                      <option value="500k-1m">$500K - $1M</option>
                      <option value="1m+">$1M+</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.08em] text-cream/60 mb-1.5">Notes</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Tell us about your investment goals..." className="w-full px-4 py-3 rounded-xl bg-emerald-950/60 border border-emerald-700/40 text-sm text-cream placeholder-cream/40 focus:outline-none focus:border-gold/60 transition-colors" />
                </div>
              </div>
              <div className="mt-6">
                <GoldButton type="submit" disabled={submitting} className="!w-full !py-4 !text-sm" data-testid="investor-submit-btn">
                  {submitting ? "Submitting..." : "Register Interest"} {!submitting && <ArrowRight className="h-4 w-4 ml-1 inline" />}
                </GoldButton>
              </div>
              <p className="mt-3 text-xs text-center text-cream/40 flex items-center justify-center gap-1.5">
                <Lock className="h-3 w-3" /> Investment opportunities are available to accredited investors only.
              </p>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
